import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { useStrictLocalization } from "@faire/web/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { formatMoney, moneyOrZero } from "@faire/web/common/money";
import { NonBreaking } from "@faire/web/common/string";
import { Lock } from "@faire/web/slate/icons/foundation/Lock";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Tooltip } from "@faire/web/slate/Tooltip";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React from "react";
import { useMemo } from "react";
import styled from "styled-components";

import { LoadableTypography } from "@faire/retailer/components/GhostLoading/LoadableTypography";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

export const LoggedOutAdditionalProductDetails = ({
  productTile,
}: {
  productTile: ILeanProductTile | undefined;
}) => {
  const { strictLocalize } = useStrictLocalization();
  const isProductLoaded = !!productTile?.product;

  const { displayCurrency, isUK: isUKRetailer } = useRetailerStore([
    "displayCurrency",
    "isUK",
  ]);

  const retailPrice = useMemo(() => {
    if (productTile?.is_locked_view) {
      return "";
    }

    if (!productTile?.product) {
      if (isUKRetailer) {
        return strictLocalize({
          defaultMessage: "Loading RRP",
          description: {
            text: "Indicates that the Recommended Retailer Price (RRP) is loading.",
          },
        });
      }
      return strictLocalize({
        defaultMessage: "Loading MSRP",
        description: {
          text: "Indicates that the manufacturer's suggested retail price (MSRP) is loading.",
        },
      });
    }

    if (isUKRetailer) {
      return strictLocalize(
        {
          defaultMessage: "RRP{nbsp}{retailPrice}",
          description: {
            text: "The Recommended Retailer Price (RRP) is the price that a product's manufacturer recommends it be sold for at point of sale.",
          },
        },
        DEPRECATED_backwardsCompatibleValues({
          retailPrice: formatMoney({
            money: moneyOrZero(
              productTile?.min_option_retail_price,
              displayCurrency
            ),
            omitCentsIfZero: true,
          }),
          nbsp: NonBreaking.space,
        })
      );
    }

    return strictLocalize(
      {
        defaultMessage: "MSRP{nbsp}{retailPrice}",
        description: {
          text: "The manufacturer's suggested retail price (MSRP) is the price that a product's manufacturer recommends it be sold for at point of sale.",
        },
      },
      DEPRECATED_backwardsCompatibleValues({
        retailPrice: formatMoney({
          money: moneyOrZero(
            productTile?.min_option_retail_price,
            displayCurrency
          ),
          omitCentsIfZero: true,
        }),
        nbsp: NonBreaking.space,
      })
    );
  }, [
    productTile?.is_locked_view,
    productTile?.product,
    productTile?.min_option_retail_price,
    isUKRetailer,
    strictLocalize,
    displayCurrency,
  ]);

  return (
    <>
      <ProductDetailsRow justify="flex-start">
        <LoadableTypography
          data-test-id="retailPrice"
          isLoaded={isProductLoaded}
        >
          {retailPrice}
        </LoadableTypography>
        <Spacer width="1x" />
        <ResponsiveLock />
      </ProductDetailsRow>
    </>
  );
};

const ProductDetailsRow = styled(Row).attrs({ align: "center" })`
  position: relative;
  width: 100%;
`;

const ResponsiveLock = () => {
  const { strictLocalize } = useStrictLocalization();
  const { isMobile } = useViewport();

  if (isMobile) {
    return <Lock lockType="foundation" />;
  }

  return (
    <Tooltip
      position="top"
      contents={[
        {
          body: strictLocalize({
            defaultMessage: "Sign up for Faire to unlock wholesale pricing",
          }),
        },
      ]}
      icon={<Lock lockType="foundation" />}
      width={220}
    />
  );
};
